/**
 * The dashboard's two week-ahead panels (admin doc 05, the design's "Today's bookings by state"
 * and "Supply liquidity").
 *
 * **The server counts statuses; the console bands them.** A band is a presentation choice, and a
 * status the server adds tomorrow must still show up — so anything this file does not name lands
 * in "Other" rather than disappearing from the total.
 */
import type { Tone } from '../components/primitives';

export type Band = { label: string; tone: Tone; count: number };

/** The design's bands, in its order, plus the two it had no row for. */
const BANDS: readonly { label: string; tone: Tone; statuses: readonly string[] }[] = [
  { label: 'Requested', tone: 'info', statuses: ['requested', 'matching'] },
  {
    label: 'Awaiting pandit',
    tone: 'warn',
    statuses: ['acceptance_expired', 'no_provider_available', 'reassignment_required'],
  },
  { label: 'Awaiting payment', tone: 'info', statuses: ['accepted', 'payment_pending'] },
  { label: 'Confirmed', tone: 'success', statuses: ['confirmed'] },
  { label: 'On the way', tone: 'info', statuses: ['provider_en_route'] },
  { label: 'In progress', tone: 'info', statuses: ['in_progress'] },
  {
    label: 'Completed today',
    tone: 'success',
    statuses: ['completed', 'issue_window', 'payout_approved', 'settled'],
  },
  {
    label: 'Cancelled',
    tone: 'neutral',
    statuses: ['customer_cancelled', 'pandit_cancelled', 'refund_pending', 'partially_refunded'],
  },
  { label: 'Disputed or no-show', tone: 'error', statuses: ['disputed', 'no_show'] },
];

const BANDED = new Set(BANDS.flatMap((b) => b.statuses));

export function stateBands(today: Readonly<Record<string, number>>): Band[] {
  const sum = (statuses: readonly string[]) => statuses.reduce((n, s) => n + (today[s] ?? 0), 0);
  const bands: Band[] = BANDS.map((b) => ({ label: b.label, tone: b.tone, count: sum(b.statuses) }));
  const other = sum(Object.keys(today).filter((s) => !BANDED.has(s)));
  if (other > 0) bands.push({ label: 'Other', tone: 'neutral', count: other });
  return bands;
}

/** The design's thresholds: above 70% is healthy, above 40% is thin, anything less is failing. */
export function coverTone(share: number | null): Tone {
  if (share === null) return 'neutral';
  if (share > 0.7) return 'success';
  if (share > 0.4) return 'warn';
  return 'error';
}

/**
 * What the number is, said plainly. The design's "% of slots coverable" needs calendars the server
 * does not read here; what it does know is how many of the week's bookings already have a pandit.
 */
export function coverLabel(share: number | null): string {
  if (share === null) return 'Nothing booked this week';
  return `${Math.round(share * 100)}% of bookings have a pandit`;
}
